import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";
import { StarRating } from "./StarRating";
import { toast } from "sonner";

export default function Reviews({ service }) {
  const { user, authHeader } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [eligible, setEligible] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    const r = await axios.get(`${API}/reviews/service/${service.id}`);
    setReviews(r.data);
  };

  useEffect(() => {
    load();
    if (user?.role === "customer") {
      axios.get(`${API}/bookings/customer`, { headers: authHeader() })
        .then((r) => {
          const has = r.data.some((b) => b.service_id === service.id && b.status === "completed");
          setEligible(has);
        }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [service.id, user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reviews`, { service_id: service.id, rating, comment: comment || null }, { headers: authHeader() });
      toast.success("Review posted");
      setComment(""); setRating(5);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post");
    }
  };

  const report = async (id) => {
    try {
      if (!reason || !reason.trim()) {
        toast.error("Please describe how this review violates the guidelines");
        return;
      }
      await axios.post(`${API}/reviews/${id}/report`, { reason }, { headers: authHeader() });
      toast.success("Reported — BookFreely will review it");
      setDeleting(null); setReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const isOwner = user?.role === "business" && user?.id === service.business_id;
  const inputCls = "w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]";

  return (
    <div data-testid="reviews-section" className="mt-10">
      <h2 className="font-display text-2xl" style={{ color: "var(--gf-text)" }}>Reviews</h2>

      {eligible && (
        <form onSubmit={submit} className="mt-4 rounded-xl border p-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
          <div className="flex items-center gap-2">
            {[1,2,3,4,5].map((i) => (
              <button key={i} type="button" data-testid={`star-${i}`} onClick={() => setRating(i)} style={{ color: i <= rating ? "var(--gf-accent)" : "var(--gf-text-muted)" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill={i <= rating ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5"><polygon points="12,2 15,9 22,9.5 17,14.5 18.5,22 12,18 5.5,22 7,14.5 2,9.5 9,9"/></svg>
              </button>
            ))}
          </div>
          <textarea data-testid="review-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience (optional)..."
            className={inputCls + " mt-3"} style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
          <button data-testid="post-review-btn" className="mt-3 rounded-full font-medium px-5 py-2" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>Post review</button>
        </form>
      )}

      <div className="mt-5 space-y-3">
        {reviews.length === 0 ? (
          <div className="text-sm" style={{ color: "var(--gf-text-muted)" }}>No reviews yet.</div>
        ) : reviews.map((r) => (
          <div key={r.id} data-testid={`review-${r.id}`} className="rounded-xl border p-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <StarRating value={r.rating} count={0} />
                <span className="font-medium" style={{ color: "var(--gf-text)" }}>{r.customer_name}</span>
              </div>
              {isOwner && (deleting === r.id ? (
                <div className="flex items-center gap-2">
                  <input data-testid={`reason-${r.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why does this violate guidelines?" className="text-xs border rounded-md px-2 py-1 bg-transparent" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
                  <button data-testid={`confirm-report-${r.id}`} onClick={() => report(r.id)} className="text-xs text-red-500">Submit report</button>
                  <button onClick={() => { setDeleting(null); setReason(""); }} className="text-xs" style={{ color: "var(--gf-text-muted)" }}>Cancel</button>
                </div>
              ) : (
                <button data-testid={`report-review-${r.id}`} onClick={() => setDeleting(r.id)} className="text-xs hover:text-red-500" style={{ color: "var(--gf-text-muted)" }} title="Report this review for guideline violation">Report</button>
              ))}
            </div>
            {r.comment && <p className="text-sm mt-2" style={{ color: "var(--gf-text-soft)" }}>{r.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
